const GEOMETRY_PRECISION = 4
const CALIBRATION_CHANGE_EPSILON = 0.000001

type SectionVertex = Record<string, unknown> & {
  x?: unknown
  y?: unknown
}

export type CalibratableSection = Record<string, unknown> & {
  width?: unknown
  height?: unknown
  offsetX?: unknown
  offsetY?: unknown
  radius?: unknown
  rotation?: unknown
  vertices?: SectionVertex[] | null
}

const roundGeometryValue = (value: number) => Number(value.toFixed(GEOMETRY_PRECISION))

const scaleFormValue = (value: unknown, ratio: number) => {
  if (value === null || value === undefined || value === '') return value

  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return value

  return roundGeometryValue(numericValue * ratio).toString()
}

const scaleVertexValue = (value: unknown, ratio: number) => {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return value

  return roundGeometryValue(numericValue * ratio)
}

export const isValidPixelToSize = (value: unknown): boolean => {
  const numericValue = Number(value)
  return Number.isFinite(numericValue) && numericValue > 0
}

export const hasCalibrationChanged = (previousValue: number, nextValue: number) =>
  Math.abs(previousValue - nextValue) > CALIBRATION_CHANGE_EPSILON

export const recalculateSectionGeometry = (
  section: CalibratableSection,
  previousPixelToSize: number,
  nextPixelToSize: number
): CalibratableSection => {
  if (!isValidPixelToSize(previousPixelToSize) || !isValidPixelToSize(nextPixelToSize)) {
    return section
  }

  const ratio = nextPixelToSize / previousPixelToSize
  const vertices = Array.isArray(section.vertices)
    ? section.vertices.map((vertex) => ({
        ...vertex,
        x: scaleVertexValue(vertex.x, ratio),
        y: scaleVertexValue(vertex.y, ratio),
      }))
    : section.vertices

  return {
    ...section,
    width: scaleFormValue(section.width, ratio),
    height: scaleFormValue(section.height, ratio),
    offsetX: scaleFormValue(section.offsetX, ratio),
    offsetY: scaleFormValue(section.offsetY, ratio),
    ...('radius' in section && {
      radius: scaleFormValue(section.radius, ratio),
    }),
    ...('vertices' in section && { vertices }),
  }
}

export const clearSectionGeometry = (section: CalibratableSection): CalibratableSection => ({
  ...section,
  width: null,
  height: null,
  offsetX: null,
  offsetY: null,
  radius: null,
  vertices: null,
  rotation: '0',
})
